import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Check, Loader2 } from "lucide-react";
import { Reveal, LineReveal } from "../components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const deleted = [
  "Your profile (name, email, bio, photo, specialty, license info)",
  "All listings you posted (and the photos attached)",
  "All applications you submitted",
  "All messages you sent or received",
  "Your saved listings / favorites",
  "Push notification tokens for your devices",
  "Referral code, credit balance, and referral ledger",
];

const retained = [
  ["Financial transaction records", "IAP purchase history is retained 7 years for tax and audit compliance, but anonymized (linked to a hash, not your name/email)."],
  ["Server logs older than 30 days", "Already auto-deleted."],
];

export default function DeleteAccount() {
  const [form, setForm] = useState({ email: "", full_name: "", reason: "" });
  const [ackPermanent, setAckPermanent] = useState(false);
  const [ackSub, setAckSub] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.full_name) {
      toast.error("Please fill in your account email and full name.");
      return;
    }
    if (!ackPermanent || !ackSub) {
      toast.error("Please confirm both acknowledgements before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/deletion-requests`, {
        email: form.email,
        full_name: form.full_name,
        reason: form.reason || null,
        ack_permanent: ackPermanent,
        ack_subscription: ackSub,
      });
      setDone(true);
      toast.success("Deletion request received. We'll respond within 5 business days.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong. Please try again or email privacy@probeauty.app.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative z-10 pt-28 md:pt-36 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Link
          to="/"
          data-testid="back-to-legal-home"
          className="group inline-flex items-center gap-2 font-mono-meta text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A] hover:text-[#C6826D] transition-colors"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
          Back to legal home
        </Link>

        <div className="mt-10 md:mt-14 border-b border-[#E0E0D9] pb-12 md:pb-16">
          <div className="font-mono-meta text-[11px] uppercase tracking-[0.3em] text-[#C6826D]">
            ProBeauty Connect
          </div>
          <h1 className="mt-6 font-serif-display font-light tracking-tight leading-[0.95] text-5xl sm:text-6xl md:text-7xl">
            <LineReveal lines={["Delete Your", "Account"]} />
          </h1>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-lg text-[#525252] leading-relaxed">
              You have full control over your data. Deleting your account removes
              your profile, listings, applications, messages, photos, and history
              within 30 days.
            </p>
          </Reveal>
        </div>
      </div>

      {/* warning banner */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-12">
        <Reveal>
          <div className="flex items-start gap-4 border border-[#C6826D] bg-[#F3EDE9] p-6 md:p-8">
            <AlertTriangle size={26} strokeWidth={1.5} className="text-[#A86955] shrink-0 mt-0.5" />
            <div>
              <div className="font-serif-display text-2xl md:text-3xl text-[#0A0A0A]">This is permanent</div>
              <p className="mt-2 text-[#525252] leading-relaxed max-w-2xl">
                Deleting your account removes your profile, listings, applications,
                messages, photos, and history within 30 days. This cannot be undone.
                Active Verified Pro subscriptions must be cancelled separately in
                iOS Settings → Subscriptions or Google Play Store.
              </p>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* FORM */}
        <div className="lg:col-span-7 order-2 lg:order-1">
          <div className="font-mono-meta text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A] mb-4">
            Option 1 — Delete from inside the app (fastest)
          </div>
          <ol className="legal-prose mb-14">
            <li style={{ paddingLeft: 0 }}>Open ProBeauty Connect → tap the <strong>Profile</strong> tab.</li>
            <li style={{ paddingLeft: 0 }}>Scroll to the bottom → tap <strong>Settings</strong>.</li>
            <li style={{ paddingLeft: 0 }}>Tap <strong>Delete Account</strong> → confirm with your password.</li>
          </ol>

          <div className="font-mono-meta text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A] mb-6">
            Option 2 — Request via this form
          </div>

          {done ? (
            <motion.div
              data-testid="deletion-success"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-[#0A0A0A] bg-[#0A0A0A] text-[#F9F9F6] p-8 md:p-10"
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-[#C6826D]">
                  <Check size={18} />
                </span>
                <div className="font-serif-display text-3xl">Request received</div>
              </div>
              <p className="mt-5 text-[#F9F9F6]/80 leading-relaxed max-w-lg">
                We've logged your account deletion request for <strong className="text-[#F9F9F6]">{form.email}</strong>.
                We verify the request from the email associated with your account and
                respond within 5 business days. Data is purged within 30 days.
              </p>
              <button
                data-testid="submit-another"
                onClick={() => { setDone(false); setForm({ email: "", full_name: "", reason: "" }); setAckPermanent(false); setAckSub(false); }}
                className="mt-8 border border-[#F9F9F6]/40 px-6 py-3 text-sm hover:bg-[#F9F9F6] hover:text-[#0A0A0A] transition-colors"
              >
                Submit another request
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="deletion-form" className="border border-[#E0E0D9] bg-[#F9F9F6] p-8 md:p-10">
              <p className="text-sm text-[#525252] mb-8">
                If you don't have the app installed anymore, use the form below. We
                verify the request from the email associated with your account and
                respond within 5 business days.
              </p>

              <div className="mb-7">
                <label htmlFor="email" className="block font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3">
                  Account email <span className="text-[#C6826D]">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  data-testid="input-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  className="w-full bg-transparent border border-[#E0E0D9] rounded-none px-4 py-3.5 text-[#0A0A0A] placeholder:text-[#B5B5AD] focus:outline-none focus:border-[#0A0A0A] transition-colors"
                />
                <p className="mt-2 text-xs text-[#8A8A8A]">Must match the email on your ProBeauty Connect account.</p>
              </div>

              <div className="mb-7">
                <label htmlFor="full_name" className="block font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3">
                  Full name on the account <span className="text-[#C6826D]">*</span>
                </label>
                <input
                  id="full_name"
                  type="text"
                  required
                  data-testid="input-fullname"
                  value={form.full_name}
                  onChange={update("full_name")}
                  placeholder="Marcus Rivera"
                  className="w-full bg-transparent border border-[#E0E0D9] rounded-none px-4 py-3.5 text-[#0A0A0A] placeholder:text-[#B5B5AD] focus:outline-none focus:border-[#0A0A0A] transition-colors"
                />
              </div>

              <div className="mb-8">
                <label htmlFor="reason" className="block font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3">
                  Reason (optional)
                </label>
                <textarea
                  id="reason"
                  rows={3}
                  data-testid="input-reason"
                  value={form.reason}
                  onChange={update("reason")}
                  placeholder="Help us improve — why are you leaving?"
                  className="w-full bg-transparent border border-[#E0E0D9] rounded-none px-4 py-3.5 text-[#0A0A0A] placeholder:text-[#B5B5AD] focus:outline-none focus:border-[#0A0A0A] transition-colors resize-none"
                />
              </div>

              <label className="flex items-start gap-3 mb-5 cursor-pointer group">
                <input
                  type="checkbox"
                  data-testid="ack-permanent"
                  checked={ackPermanent}
                  onChange={(e) => setAckPermanent(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#C6826D] shrink-0"
                />
                <span className="text-sm text-[#525252] leading-relaxed">
                  I understand that deleting my account is permanent, will remove all
                  my listings, applications, messages, and photos, and cannot be undone.
                </span>
              </label>

              <label className="flex items-start gap-3 mb-8 cursor-pointer group">
                <input
                  type="checkbox"
                  data-testid="ack-subscription"
                  checked={ackSub}
                  onChange={(e) => setAckSub(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#C6826D] shrink-0"
                />
                <span className="text-sm text-[#525252] leading-relaxed">
                  I have cancelled any active Verified Pro subscription in iOS Settings
                  or Google Play Store (or I have no active subscription).
                </span>
              </label>

              <button
                type="submit"
                data-testid="submit-deletion"
                disabled={submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-[#F9F9F6] px-8 py-4 text-sm tracking-tight transition-colors duration-300 hover:bg-[#C6826D] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {submitting ? "Submitting…" : "Submit deletion request"}
              </button>

              <p className="mt-5 text-xs text-[#8A8A8A]">
                Alternatively, email <a href="mailto:privacy@probeauty.app" className="text-[#C6826D] underline">privacy@probeauty.app</a> with subject “Account deletion request”.
              </p>
            </form>
          )}
        </div>

        {/* SIDE: what gets deleted / retained */}
        <div className="lg:col-span-5 order-1 lg:order-2 space-y-12">
          <Reveal>
            <div className="font-mono-meta text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A] mb-5">
              What gets deleted
            </div>
            <ul className="space-y-3">
              {deleted.map((d) => (
                <li key={d} className="flex items-start gap-3 text-[#525252] leading-relaxed">
                  <span className="mt-1.5 w-1.5 h-1.5 bg-[#C6826D] rotate-45 shrink-0" />
                  <span className="text-sm">{d}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal>
            <div className="font-mono-meta text-[11px] uppercase tracking-[0.25em] text-[#8A8A8A] mb-5">
              What is retained (and why)
            </div>
            <div className="space-y-5">
              {retained.map(([t, d]) => (
                <div key={t} className="border-l-2 border-[#E0E0D9] pl-5">
                  <div className="font-serif-display text-xl text-[#0A0A0A]">{t}</div>
                  <p className="mt-1 text-sm text-[#525252] leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <div className="border border-[#E0E0D9] bg-[#F3F3EF] p-6">
              <div className="font-mono-meta text-[11px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3">
                Subscription cancellation
              </div>
              <p className="text-sm text-[#525252] leading-relaxed">
                If you have an active Verified Pro subscription, you must cancel it
                separately — Apple and Google won't let us cancel on your behalf.
              </p>
              <p className="text-sm text-[#525252] leading-relaxed mt-3">
                <strong className="text-[#0A0A0A]">iOS:</strong> Settings → tap your name → Subscriptions → ProBeauty Connect → Cancel.
              </p>
              <p className="text-sm text-[#525252] leading-relaxed mt-1">
                <strong className="text-[#0A0A0A]">Android:</strong> Google Play Store → Profile → Payments & Subscriptions → Subscriptions → ProBeauty Connect → Cancel.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
